import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Sheet, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { Approval, DurationInMilliseconds } from 'constants/General'
import { useRefundAuthMutation, useRefundMutation } from 'hooks/mutations/useRefundMutation'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { RefundAuthRequest } from 'types/refund'
import { Transaction } from 'types/transaction'
import { ActionIds, WalletType } from 'types/types'
import { generateReference, roundNumberWithCommas } from 'utils/utils'
import { RefundFormPayload, refundSchema } from '../schemas/refundSchema'
import { RefundStep, RefundSteps } from '../types'
import { RefundComplete } from './RefundComplete'
import { RefundConfirmation } from './RefundConfirmation'
import { RefundForm } from './RefundForm'

interface RefundSheetProps {
  wallet: WalletType
  transaction: Transaction
  isRefundSheetOpen: boolean
  isPartiallyRefundable: boolean
  setIsRefundSheetOpen: Dispatch<SetStateAction<boolean>>
  setIsSheetOpen: Dispatch<SetStateAction<boolean>>
}

export const RefundSheet = ({
  wallet,
  transaction,
  isRefundSheetOpen,
  isPartiallyRefundable,
  setIsRefundSheetOpen,
  setIsSheetOpen,
}: RefundSheetProps) => {
  const { t } = useTranslation()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [_, setWalletModalAction] = useState<WalletAction>(undefined)
  const [submitRefundError, setSubmitRefundError] = useState<BaseErrorResponse>(null)
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false)
  const [step, setStep] = useState<RefundStep>(RefundSteps.FORM)
  const [walletBalanceIsInsufficient, setWalletBalanceIsInsufficient] = useState(false)
  const { twoFactorEnabled } = useTwoFAContext()

  const handleOpenConvertSheet = () => {
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenConvertModal = () => {
    setIsRefundSheetOpen(false)
    setWalletModalAction({
      key: 'convert',
    })
  }

  const queryClient = useQueryClient()

  const refundFormSchema = refundSchema(t)
  const form = useForm<RefundFormPayload>({
    resolver: yupResolver(refundFormSchema),
    mode: 'onChange',
  })

  const { control, clearErrors } = form

  const formValues = useWatch({ control }) as RefundFormPayload

  const isFirstParty = !transaction?.context?.depositDetails?.isThirdParty

  const isRefundForm = step === RefundSteps.FORM
  const isApprovalsEnabled = step === RefundSteps.APPROVALS
  const isRefundConfirmation = step === RefundSteps.CONFIRMATION

  const amountSummary = {
    amount: transaction?.amount,
    currency: wallet?.currency?.code,
  }
  const approvalType = isFirstParty
    ? Approval.FLOWS.FIAT_1ST_PARTY_PAYOUT
    : Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT

  const { approvalsIsRequiredForFlow, isFetchingApprovalPreference } = useApprovalsConfigForFlow(
    approvalType,
    amountSummary
  )

  const isRequiresApprovalModalVisible = approvalsIsRequiredForFlow && !isFetchingApprovalPreference

  const { mutate: createRefundRequest } = useRefundMutation()

  const {
    mutate: createRefundAuthRequest,
    isPending: isCreateRefundAuthRequestRequestLoading,
    isError: isOtpRequestError,
    reset: resetRefundAuthMutation,
  } = useRefundAuthMutation()

  const handleRefundPreRequest = password => {
    const amountValue = parseFloat(formValues?.amount).toFixed(2)
    const amountAsNumber = Number(amountValue)
    const refundAmount = !isNaN(amountAsNumber) ? Number(amountValue) : Number(transaction?.amount)

    const refundRequest = {
      data: {
        paymentReference: formValues?.reference || generateReference(),
        reason: 'Refund',
        amount: {
          value: refundAmount,
          currency: transaction?.wallet?.currency?.code,
        },
      },
      transactionReference: transaction?.context?.reference,
      twoFactorEnabled: twoFactorEnabled,
    }
    createRefundRequest(refundRequest, {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['walletTransactions'] })
        if (isRequiresApprovalModalVisible) {
          setStep(RefundSteps.APPROVALS)
        } else {
          handleClose()
        }
        toast({
          header: t('refundSuccessful', {
            refundAmount: roundNumberWithCommas(formValues?.amount || transaction?.amount),
            currency: transaction?.wallet?.currency?.code,
          }),
          variant: 'info',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
      },
      onError: async (error: BaseErrorResponse) => {
        if (error?.status === 428) {
          const refundAuthRequest: RefundAuthRequest = {
            challenge: error?.data?.challenge?.value,
            tokenResponse: password ?? error?.data?.code,
            mfaCallbackUrl: error?.data?.callbackUrl,
          }
          handleRefundPostRequest(refundAuthRequest)
          return
        }
        if (error?.status === 403 || error?.status === 400) {
          toast({
            header: t('oopsSomethingWentWrong'),
            variant: 'error',
            duration: DurationInMilliseconds.TEN_SECONDS,
          })
        }
      },
    })
  }

  const handleRefundPostRequest = (authRequest: RefundAuthRequest) => {
    createRefundAuthRequest(authRequest, {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['walletTransactions'] })
        if (isRequiresApprovalModalVisible) {
          setStep(RefundSteps.APPROVALS)
        } else {
          handleClose()
        }
        toast({
          header: t('refundSuccessful', {
            refundAmount: roundNumberWithCommas(formValues?.amount || transaction?.amount),
            currency: transaction?.wallet?.currency?.code,
          }),
          variant: 'info',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
      },
      onError: (error: BaseErrorResponse) => {
        setSubmitRefundError(error)
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        logSentryError('Error: createRefundAuthRequest', error, {
          authRequest,
        })
      },
    })
  }
  useEffect(() => {
    if (formValues?.password?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      handleRefundPreRequest(formValues?.password)
    }
  }, [formValues?.password, twoFactorEnabled])

  const onPasswordSubmit = () => {
    if (!twoFactorEnabled) {
      handleRefundPreRequest(formValues?.password)
    }
  }

  const title = useMemo(() => {
    if (isApprovalsEnabled || (walletBalanceIsInsufficient && !isPartiallyRefundable)) return
    if (isRefundForm) {
      if (isPartiallyRefundable) {
        return t('refundPartial', {
          recipient: transaction?.context?.depositDetails?.senderName,
        })
      } else {
        return t('refundAmount', {
          refundAmount: roundNumberWithCommas(transaction?.amount),
          currency: transaction?.wallet?.currency?.code,
          recipient: transaction?.context?.depositDetails?.senderName,
        })
      }
    } else {
      return t('confirmRefund', {
        recipient: transaction?.context?.depositDetails?.senderName,
      })
    }
  }, [transaction, isPartiallyRefundable, step, walletBalanceIsInsufficient])

  const handleClose = () => {
    setIsRefundSheetOpen(false)
    setSubmitRefundError(null)
    resetRefundAuthMutation()
    clearErrors()
    setStep(RefundSteps.FORM)
  }

  const onBack = () => {
    if (isRefundConfirmation) {
      setStep(RefundSteps.FORM)
      clearErrors()
    }
    if (isRefundForm) {
      handleClose()
    }
  }

  const description = useMemo(() => {
    if (isApprovalsEnabled || (walletBalanceIsInsufficient && !isPartiallyRefundable)) return

    return {
      subTitle: t('refundWallet', {
        walletName: transaction?.wallet?.description,
      }),
    }
  }, [isApprovalsEnabled, transaction, walletBalanceIsInsufficient])

  return (
    <Sheet
      header={title}
      onBack={onBack}
      isOpen={isRefundSheetOpen}
      onClose={handleClose}
      subHeader={description}
    >
      <Box width="full">
        {step === RefundSteps.FORM && (
          <RefundForm
            form={form}
            wallet={wallet}
            transaction={transaction}
            formValues={formValues}
            isPartiallyRefundable={isPartiallyRefundable}
            isRefundSheetOpen={isRefundSheetOpen}
            isInsufficientBalance={isInsufficientBalance}
            setStep={setStep}
            setIsInsufficientBalance={setIsInsufficientBalance}
            handleOpenConvertSheet={handleOpenConvertSheet}
            handleOpenConvertModal={handleOpenConvertModal}
            handleClose={handleClose}
            setWalletBalanceIsInsufficient={setWalletBalanceIsInsufficient}
          />
        )}
        {step === RefundSteps.CONFIRMATION && (
          <RefundConfirmation
            form={form}
            formValues={formValues}
            wallet={wallet}
            isCreateRefundAuthRequestRequestLoading={isCreateRefundAuthRequestRequestLoading}
            submitRefundError={submitRefundError}
            isOtpRequestError={isOtpRequestError}
            handleClose={handleClose}
            onPasswordSubmit={onPasswordSubmit}
          />
        )}
        {step === RefundSteps.APPROVALS && <RefundComplete handleClose={handleClose} />}
      </Box>
    </Sheet>
  )
}
